'use client'

import { useMemo } from 'react'
import { useParams } from 'next/navigation'
import { Sorter } from '@/components/Products/Series/SeriesProductsList/SeriesProductsCard/SeriesProductsCardItemsList/SeriesProductsCardItemsList'
import {
	Button,
	ClippedImage,
	Copy,
	RightTopArrow,
	Tag,
	cn,
} from '@desoutter/uikit'
import { Specification } from '@/app/api/types/redpim.types'
import { LocalizedLink } from '@/components/LocalizedLink'
import { toast } from 'sonner'
import { FileText } from 'lucide-react'
import { useUnitContext } from '@/hooks/useUnitContext'

type ProductListItemProps = {
	partnumber: string
	model: string
	specifications?: Specification[]
	specificationBlocks?: any
	sorters?: Sorter[]
	imageUrl?: string
	className?: string
	partNumberCopiedToClipboard?: string
	getAQuote?: string
	viewProductPage?: string
}

export default function ProductListItem({
	partnumber,
	model,
	specifications,
	specificationBlocks,
	sorters,
	imageUrl,
	className,
	partNumberCopiedToClipboard,
	getAQuote,
	viewProductPage,
}: ProductListItemProps) {
	const { lang } = useParams()
	const { unit } = useUnitContext()

	// Utility function to get specification value with unit context
	const getSpecValueWithUnit = (spec: Specification) => {
		// If we have specificationBlocks, try to get the unit-specific value
		if (specificationBlocks) {
			const specName =
				spec.nameIntl?.[lang as keyof typeof spec.nameIntl] ||
				spec.nameIntl?.en_GB ||
				spec.nameIntl?.en_US ||
				Object.values(spec.nameIntl || {})[0]

			if (specName) {
				// Prioritize the current unit context, then Universal, then fallback to other units
				const blockValue =
					(unit && specificationBlocks[unit]?.[specName]) ||
					specificationBlocks.Universal?.[specName] ||
					(unit === 'Metric' && specificationBlocks.Metric?.[specName]) ||
					(unit === 'Imperial' && specificationBlocks.Imperial?.[specName])

				if (blockValue) {
					return blockValue
				}
			}
		}

		// Fallback to the specification value itself
		if (spec.value) {
			return `${spec.value}${spec?.unit ? ' ' + spec.unit : ''}`
		}
		return '-'
	}

	const formatSpecifications: { attributeId: string; value: string }[] =
		useMemo(() => {
			if (!sorters) return []

			if (!specifications) {
				return sorters.slice(2).map((sorter) => ({
					attributeId: sorter.value,
					value: '-',
				}))
			}

			return sorters.reduce((acc, sorter) => {
				if (sorter.value === 'model' || sorter.value === 'partnumber') {
					return acc
				}
				const spec = specifications.find(
					(spec) => spec.attributeId === sorter.value,
				)
				if (spec) {
					acc.push({
						attributeId: spec.attributeId,
						value: getSpecValueWithUnit(spec),
					})
				} else {
					acc.push({
						attributeId: sorter.value,
						value: '-',
					})
				}

				return acc.sort((a, b) => {
					let indexA = sorters.findIndex(
						(sorter) => sorter.value === a.attributeId,
					)
					let indexB = sorters.findIndex(
						(sorter) => sorter.value === b.attributeId,
					)
					return indexA - indexB
				})
			}, [])
		}, [specifications, sorters, unit, specificationBlocks, lang])

	return (
		<div
			className={cn(
				'bg-gray-50 rounded-2xl flex md:h-20 min-w-full w-max gap-6 px-3 py-3 justify-between',
				className,
			)}
		>
			<LocalizedLink
				locale={lang}
				href={`/products/${partnumber}`}
				className="w-full"
				prefetch={false}
				rel="nofollow"
			>
				<div className="flex gap-4 md:gap-8 flex-1">
					<div className="flex items-center w-14 stretch-0">
						<ClippedImage
							src={imageUrl}
							alt="product"
							width={56}
							height={56}
							sourceWidth={400}
							sourceHeight={400}
							className="rounded-lg"
						/>
					</div>

					<div className="flex flex-1 gap-8">
						<div className="flex items-center w-40">
							<div className="h5 font-bold m-0 ml-auto w-64 text-md line-clamp-1">
								{model}
							</div>
						</div>
						<div className="flex items-center justify-center w-24">
							<div
								className="bg-foreground text-background rounded flex items-center justify-center gap-1 px-1"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									navigator.clipboard.writeText(partnumber)
									toast.success(
										partNumberCopiedToClipboard ||
											'Part number copied to clipboard',
									)
								}}
							>
								<Tag
									variant="tertiary"
									label={partnumber}
									className="w-fit text-center px-0"
								/>

								<div className="bg-foreground text-background flex items-center">
									<Copy xs />
								</div>
							</div>
						</div>
						{formatSpecifications.map((sorter, index) => (
							<div
								key={index}
								className="flex items-center justify-center w-24 flex-grow"
							>
								<span className="text-center line-clamp-2">{sorter.value}</span>
							</div>
						))}
					</div>
				</div>
			</LocalizedLink>
			<div className="flex items-center gap-4 w-36">
				<LocalizedLink href={`/get-a-quote?pn=${partnumber}`} locale={lang}>
					<Button
						title={getAQuote}
						variant="outline"
						color="tertiary"
						className="border-gray-300"
						icon={() => <FileText className="w-4 h-4" />}
						size="md"
					/>
				</LocalizedLink>
				<LocalizedLink
					locale={lang}
					href={`/products/${partnumber}`}
					className="w-full"
					prefetch={false}
					rel="nofollow"
				>
					<Button
						title={viewProductPage}
						color="secondary"
						icon={RightTopArrow}
						size="md"
					/>
				</LocalizedLink>
			</div>
		</div>
	)
}
